import { useClientProduct } from "api/Client/product";
import LogoSpinner from "components/Spinner/LogoSpinner";
import { image_host } from "config/api_host";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Cpu,
  HardDrive,
  StoutIcons as Memory,
  Monitor,
  ShieldCheck,
  ShoppingCart,
} from "lucide-react";
import { useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { addToCart, removeFromCart } from "store/reducers/cartReducer";

// قاموس أيقونات للمواصفات لجعل العرض بصرياً
const specIcons = {
  CPU: <Cpu size={18} />,
  RAM: <Monitor size={18} />, // أو استبدلها بأيقونة ذاكرة مناسبة
  HARD: <HardDrive size={18} />,
  GPU: <Monitor size={18} />,
};

export default function ProductView() {
  const { laptopId } = useParams();
  const dispatch = useDispatch();
  const myProducts = useSelector((state) => state.myCart.myProducts);
  const { role } = useSelector((state) => state.auth);
  const { data: productInfo, isLoading: productIsLoading } =
    useClientProduct(laptopId);

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  if (productIsLoading) return <LogoSpinner />;

  const product = {
    ...productInfo.data.product,
    price: productInfo.data.price,
    disPrice: productInfo.data.discount_price,
  };
  const gallery = product.gallery || [];
  const fullImages = [product.image, ...gallery.map((g) => g.name)];

  const next = () => setIndex((index + 1) % fullImages.length);
  const prev = () =>
    setIndex((index - 1 + fullImages.length) % fullImages.length);

  const isInCart = myProducts.some((item) => item.id === product.id);

  return (
    <div className="pt-8 pb-20 max-w-6xl w-full mx-auto font-[Tajawal] px-4">
      {/* Product Header Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row justify-between gap-8 bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl shadow-2xl p-8 relative overflow-hidden"
      >
        {/* Background Glow */}
        <div className="absolute  top-0 right-0 w-64 h-64 bg-mainLight/5 rounded-full blur-[100px] -z-10"></div>

        <div className="flex flex-col md:flex-row gap-8 items-center md:items-start flex-1">
          {/* Main Product Image with Pop-out effect */}
          <motion.div
            whileHover={{ scale: 1.05, rotate: -2 }}
            className="relative group cursor-zoom-in"
            onClick={() => {
              setIndex(0);
              setOpen(true);
            }}
          >
            <div className="absolute inset-0 bg-mainLight/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <img
              src={image_host + product.image}
              alt={product.name}
              className="relative z-10 w-48 md:w-64 lg:w-80 drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] rounded-2xl border border-mainLight/40"
            />
          </motion.div>

          {/* Product Info */}
          <div className="space-y-4 text-right flex-1">
            <div>
              <span className="text-mainLight text-sm font-bold tracking-widest uppercase opacity-80">
                {product.company_name}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mt-1 leading-tight">
                {product.name}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex flex-wrap items-center gap-4">
                {product.disPrice && Number(product.disPrice) > 0 ? (
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    {/* السعر الجديد - الأكثر بروزاً */}
                    <div className="relative">
                      <p className="text-5xl font-bold text-mainLight bg-mainLight/10 px-6 py-3 rounded-2xl border border-mainLight/30 shadow-[0_0_20px_rgba(var(--mainLight-rgb),0.2)]">
                        ${Number(product.disPrice).toLocaleString()}
                      </p>
                      {/* شارة الخصم الذكية */}
                      <span className="absolute -top-3 -left-3 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-lg shadow-lg animate-pulse">
                        وفر{" "}
                        {Math.round(
                          ((product.price - product.disPrice) / product.price) *
                            100
                        )}
                        %
                      </span>
                    </div>

                    {/* السعر القديم - باهت وأصغر */}
                    <div className="flex flex-col items-start ">
                      <span className="text-xs text-gray-300 uppercase font-bold mr-1 opacity-60">
                        السعر الأصلي
                      </span>
                      <s className="text-2xl font-bold opacity-70 text-gray-50 decoration-red-600">
                        ${Number(product.price).toLocaleString()}
                      </s>
                    </div>
                  </div>
                ) : (
                  /* الحالة العادية - لا يوجد خصم */
                  <p className="text-5xl font-bold text-mainLight bg-mainLight/10 px-6 py-3 rounded-2xl border border-mainLight/30">
                    ${Number(product.price).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex flex-col text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <ShieldCheck size={14} className="text-green-500" /> ضمان
                  حقيقي
                </span>
                <span>متوفر في المخزن</span>
              </div>
            </div>

            {/* Enhanced Product Fields (Specs) */}
            <div className="pt-4 flex gap-3 flex-wrap">
              {product.fields &&
                JSON.parse(product.fields).map((f) => (
                  <motion.div
                    key={f.fieldId}
                    whileHover={{ y: -5 }}
                    className="flex flex-col items-center bg-white/5 border border-white/10 p-3 rounded-2xl min-w-[90px] backdrop-blur-sm"
                  >
                    <span className="text-mainLight mb-1 opacity-80">
                      {specIcons[f?.name.toUpperCase()] || <Cpu size={18} />}
                    </span>
                    <span className="text-[10px] text-gray-400 uppercase font-bold">
                      {f?.name}
                    </span>
                    <span className="text-white text-sm font-bold">
                      {f?.value}
                    </span>
                  </motion.div>
                ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4 justify-center lg:border-r lg:border-white/10 lg:pr-8 lg:min-w-[200px]">
          <Link
            to={role == "customer" ? "/cp/contact-us" : "/contact-us"}
            className={`flex items-center justify-center gap-2 py-4 px-6 rounded-2xl animate-bounce font-bold transition-all transform active:scale-95 shadow-md ${"bg-green-500 text-white shadow-green-400 hover:-translate-y-1"}`}
          >
            {<FaWhatsapp size={20} />}
            تواصل مباشر
          </Link>

          <Link
            to={"booking"}
            className="group relative flex items-center justify-center gap-2 py-4 px-6 rounded-2xl bg-transparent border-2 border-mainLight text-mainLight font-bold overflow-hidden transition-all hover:text-white"
          >
            <div className="absolute inset-0 bg-mainLight translate-y-[101%] group-hover:translate-y-0 transition-transform duration-300 -z-10"></div>
            احجز الآن
          </Link>

          <button
            onClick={() =>
              isInCart
                ? dispatch(removeFromCart(product.id))
                : dispatch(addToCart(product))
            }
            className={`flex items-center justify-center gap-2 py-4 px-6 rounded-2xl font-bold transition-all transform active:scale-95 shadow-xl ${
              isInCart
                ? "bg-red-500/20 border border-red-500/50 text-red-500 hover:bg-red-500 hover:text-white"
                : "bg-mainLight text-mainDark hover:shadow-mainLight/20 hover:-translate-y-1"
            }`}
          >
            {isInCart ? <X size={20} /> : <ShoppingCart size={20} />}
            {isInCart ? "إزالة من السلة" : "إضافة للسلة"}
          </button>
        </div>
      </motion.div>

      {/* Modern Gallery Section */}
      <div className="mt-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-white">معرض الصور</h2>
          <div className="h-[2px] flex-1 bg-gradient-to-l from-mainLight/50 to-transparent mr-6"></div>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-4 gap-6">
          {fullImages.map((img, i) => (
            <motion.div
              key={i}
              layoutId={`img-${i}`}
              onClick={() => {
                setIndex(i);
                setOpen(true);
              }}
              whileHover={{ y: -10 }}
              className="relative aspect-[1/2] rounded-3xl overflow-hidden cursor-pointer border_ border-white/5_ shadow-2xl group border border-mainLight/40"
            >
              <div className="absolute inset-0 bg-mainDark/40 group-hover:opacity-0 transition-opacity z-10"></div>
              <img
                src={image_host + img}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Details Section */}
      {product?.description && (
        <div className="mt-20 max-w-4xl">
          <h2 className="text-3xl font-bold text-white mb-8 border-r-4 border-mainLight pr-4 leading-none">
            التفاصيل التقنية
          </h2>
          <div
            className="prose prose-invert max-w-none text-gray-300 leading-relaxed text-lg 
          bg-white/5 p-8 rounded-3xl border border-white/10"
            dangerouslySetInnerHTML={{
              __html: product?.description?.replace(/\n/g, "<br />"),
            }}
          />
        </div>
      )}
      {/* Lightbox Modal (لم أغير المنطق، فقط حسنت التصميم البصري) */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-mainDark/95 backdrop-blur-xl 
                 flex items-center justify-center z-[1001] p-4"
          >
            {/* زر الإغلاق */}
            <button
              className="absolute top-6 right-6 text-white/60 hover:text-white transition z-20"
              onClick={() => setOpen(false)}
            >
              <X size={36} />
            </button>

            {/* الحاوية الرئيسية */}
            <motion.div
              className="relative w-full max-w-5xl flex items-center justify-center 
                   px-10 sm:px-16"
            >
              {/* السهم الأيمن */}
              <button
                onClick={prev}
                className="absolute right-1 sm:right-6 z-10 p-2 sm:p-4
                     text-white/60 hover:text-white transition
                     bg-black/30 rounded-full backdrop-blur"
              >
                <ChevronRight size={32} className="sm:size-[48px]" />
              </button>

              {/* الصورة */}
              <img
                src={image_host + fullImages[index]}
                className="w-full max-h-[70vh] sm:max-h-[80vh] 
                     object-contain rounded-xl drop-shadow-2xl"
                alt=""
              />

              {/* السهم الأيسر */}
              <button
                onClick={next}
                className="absolute left-1 sm:left-6 z-10 p-2 sm:p-4
                     text-white/60 hover:text-white transition
                     bg-black/30 rounded-full backdrop-blur"
              >
                <ChevronLeft size={32} className="sm:size-[48px]" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
